// apps/patient/src/contexts/FeatureContext.tsx
import { createContext, useContext, useState, useEffect } from 'react';
import { usePatient } from './PatientContext';
import { patientApiService } from '../services/api';
import { getCurrentPlatform } from '../config/platforms';
import { getExerciseAccessType, hasExerciseAccess } from '../utils/onboarding';
import type { PatientFeatures } from '@prism-ehr/shared';

const NOTES_HIDDEN_ORG = '3e82dbf7-0997-4c1e-86cd-2df56674d49c';

interface FeatureContextValue {
  features: PatientFeatures | null;
  isLoading: boolean;
  error: string | null;
  hasFeature: (feature: keyof PatientFeatures['enabled_features']) => boolean;
  isStandalone: boolean;
  isExercisePlatform: boolean;
  accessType: 'standalone' | 'provider_linked' | 'hybrid';
  refetchFeatures: () => Promise<void>;
}

const FeatureContext = createContext<FeatureContextValue | undefined>(undefined);

export function FeatureProvider({ children }: { children: React.ReactNode }) {
  const { patient, isAuthenticated } = usePatient();
  const [features, setFeatures] = useState<PatientFeatures | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const platform = getCurrentPlatform();
  const isExercisePlatform = platform.id === 'exercise';

  const fetchFeatures = async () => {
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Determine access type based on platform
      if (isExercisePlatform) {
        const accessType = getExerciseAccessType(patient);
        const hasAccess = hasExerciseAccess(patient);

        // Build features for exercise platform
        const exerciseFeatures: PatientFeatures = {
          patient_id: patient?.patient?.id,
          provider_id: patient?.patient?.provider_id,
          organization_id: patient?.organization_id,
          platform: 'exercise',
          access_type: accessType,
          provider_linked: accessType !== 'standalone',
          enabled_features: {
            // Clinical features - only for provider-linked users
            appointments: false, // Per spec: not included even for provider-linked
            clinical_records: false,
            prescriptions: false,

            // Communication - only for provider-linked
            messaging: accessType !== 'standalone',
            forms: accessType !== 'standalone',

            // Exercise features - based on access
            rehab_programs: accessType !== 'standalone',
            workout_plans: true,
            self_guided_workouts: hasAccess,
            community_features: false, // Future

            // New features
            create_own_workouts: hasAccess,
            progress_tracking: hasAccess,
            orders: false,
            documents: false,
            classes: false,
          },
          subscription: {
            plan: patient?.exercise_app?.subscription_plan === 'annual' ? 'premium' : 'basic',
            status: patient?.exercise_app?.subscription_status === 'active' ? 'active' : 'trial',
            features: [],
            expires_at: patient?.exercise_app?.subscription_expires_at,
          }
        };

        // If provider-linked, also check for assigned programs
        if (accessType !== 'standalone' && patient.patient_id) {
          try {
            const [activePrograms, standaloneExercises] = await Promise.all([
              patientApiService.getActivePrograms(patient.id),
              patientApiService.getStandaloneExercises(true).catch(() => []),
            ]);
            if (activePrograms.has_rehab_programs) {
              exerciseFeatures.enabled_features.rehab_programs = true;
            }
            if (activePrograms.has_workout_plans) {
              exerciseFeatures.enabled_features.workout_plans = true;
            }
            if (Array.isArray(standaloneExercises) && standaloneExercises.length > 0) {
              exerciseFeatures.enabled_features.standalone_exercises = true;
            }
          } catch (err) {
            console.warn('[FeatureContext] Could not fetch active programs:', err);
          }

          // Overlay org-driven flags (classes) from backend getFeatures()
          try {
            const backendFeatures = await patientApiService.getFeatures();
            if (backendFeatures?.enabled_features?.classes) {
              exerciseFeatures.enabled_features.classes = true;
            }
          } catch (err) {
            console.warn('[FeatureContext] Could not fetch backend features for overlay:', err);
          }
        }

        setFeatures(exerciseFeatures);
      } else {
        // Non-exercise platform - use existing API call
        const featuresData = await patientApiService.getFeatures();

        if (featuresData.provider_linked && featuresData.patient_id) {
          const [activePrograms, standaloneExercises] = await Promise.all([
            patientApiService.getActivePrograms(featuresData.patient_id).catch(() => null),
            patientApiService.getStandaloneExercises(true).catch(() => []),
          ]);
          if (activePrograms?.has_rehab_programs) {
            featuresData.enabled_features.rehab_programs = true;
          }
          if (activePrograms?.has_workout_plans) {
            featuresData.enabled_features.workout_plans = true;
          }
          if (Array.isArray(standaloneExercises) && standaloneExercises.length > 0) {
            featuresData.enabled_features.standalone_exercises = true;
          }
        }

        if (patient?.organization_id === NOTES_HIDDEN_ORG) {
          featuresData.enabled_features.clinical_records = false;
        }
        setFeatures(featuresData);
      }
    } catch (err: any) {
      console.error('[FeatureContext] Error fetching features:', err);
      setError(err.message || 'Failed to load features');

      // Default to standalone mode on error
      setFeatures({
        platform: platform.id as any,
        access_type: 'standalone',
        provider_linked: false,
        enabled_features: {
          appointments: false,
          clinical_records: false,
          messaging: false,
          prescriptions: false,
          forms: false,
          rehab_programs: false,
          workout_plans: isExercisePlatform,
          self_guided_workouts: isExercisePlatform,
          community_features: false,
          create_own_workouts: isExercisePlatform,
          progress_tracking: isExercisePlatform,
          orders: false,
          documents: false,
          classes: false,
        }
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFeatures();
  }, [isAuthenticated, patient?.patient?.id, patient?.exercise_app?.subscription_status]);

  const hasFeature = (feature: keyof PatientFeatures['enabled_features']): boolean => {
    return features?.enabled_features[feature] || false;
  };

  const accessType = features?.access_type || 'standalone';

  const value: FeatureContextValue = {
    features,
    isLoading,
    error,
    hasFeature,
    isStandalone: accessType === 'standalone',
    isExercisePlatform,
    accessType,
    refetchFeatures: fetchFeatures
  };

  return (
    <FeatureContext.Provider value={value}>
      {children}
    </FeatureContext.Provider>
  );
}

export function useFeatures() {
  const context = useContext(FeatureContext);
  if (!context) {
    throw new Error('useFeatures must be used within FeatureProvider');
  }
  return context;
}